import { Paperclip, Trash2, Upload } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import Button from "../button/Button";
import { useDropzone } from "react-dropzone";
import { FileLabelStyle, FileNameErrorStyle, FileNameStyle } from "../styles/InputStyle";
import languageEncoding from "detect-file-encoding-and-language";

interface FileUploadProps extends React.HTMLAttributes<HTMLDivElement>{  
  label?: string
  text?: string
  type: "csv"
  encoding?: "utf-8"
  error?: string
  required?: boolean
  validHeader?: Array<string>
  maximumFiles?: number
  uploadedFile: (file: File | undefined) => void
  isError: (error: boolean) => void
}

interface FileToUpload{
  file: File
  error: string | undefined
}

function FileUpload(props: Readonly<FileUploadProps>) {
  const noFilesText = "No se han adjuntado archivos.";
  const maximumFilesText = `No puede subir más de ${props.maximumFiles ?? 1} archivo${(props.maximumFiles ?? 1) > 1 ? 's' : ''} a la vez.`;
  const extensionText = `Solo se permiten archivos con formato ${props.type}.`;
  const headerText = "Cabecera del archivo incorrecto.";
  const fileLoadedText = "Archivo ya cargado.";
  const encodingText = "Solo se permiten archivos con codificación utf-8"

  const [statusText, setStatusText] = useState("");
  const [files, setFiles] = useState<Array<FileToUpload>>([]);

  const onDrop = useCallback(async (acceptedFiles:Array<File>) => {
    
    if (files && files.length + acceptedFiles.length > (props.maximumFiles ?? 1)){
      
      setStatusText(maximumFilesText);
      return;
    }

    let validHeader = props.validHeader?.join(',') ?? "";
    let newFiles: Array<FileToUpload> = [];
    newFiles.push(...files);    
    await Promise.all(
      acceptedFiles.map(async (file) => {
        if(files && files.filter(f => f.file === file).length > 0){
          setStatusText(fileLoadedText);
          return;
        }
        
        let fileHeader = (await file.text()).split('\n').shift()?.replaceAll("\r","") ?? "";

        let validationError = file.name.toLocaleLowerCase().endsWith(props.type) ? undefined: "extension";
        validationError = validationError ?? (fileHeader !== validHeader ? "header" : undefined);

        if (props.encoding === "utf-8"){
          let encoding = await languageEncoding(file).then(async (fileInfo) => {return fileInfo.encoding});
          validationError = validationError ?? encoding !== 'UTF-8'? "encoding" : undefined;          
        }
        
        newFiles.push({file: file, error: validationError});        
      })
    );

    setFiles(newFiles);
    let newHeaderText = (newFiles.filter(nh => nh.error === "header").length > 0 ? headerText : "");    
    let neweEtensionText = newFiles.filter(ne => ne.error === "extension").length > 0 ? extensionText : newHeaderText;
    let newStatusText = newFiles.filter(ne => ne.error === "encoding").length > 0 ? encodingText : neweEtensionText;
    setStatusText(newStatusText);
    if (!newStatusText || newStatusText.length === 0){
      props.uploadedFile(newFiles[0].file ?? undefined);
    }    
  }, [extensionText, files, maximumFilesText, props])

  const {getRootProps, getInputProps} = useDropzone({
    onDrop
  });

  useEffect(()=> {
    
    if (!files || files.length === 0){      
      setStatusText(statusText ?? noFilesText);      
      return;
    }

    let isValidExtension = files.filter(file => file.error === "extension").length === 0 ;
    let isValidHeader = files.filter(file => file.error === "header").length === 0 ;
    let isValidEncoding = files.filter(file => file.error === "encoding").length === 0 ;

    let newHeader = !isValidHeader ? headerText : "";
    let newExtension = !isValidExtension ? extensionText : newHeader;
    let newText = !isValidEncoding ? encodingText : newExtension;
    
    setStatusText(newText);
  },[extensionText, files, props.type, statusText]);

  useEffect(() => {
    let isError = !!(statusText && statusText.length > 0);
    props.isError(isError);
    if (isError){
      props.uploadedFile(undefined);
    }    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusText]);

  function deleteFile(file:File){    
    let newAceptedFiles = [...files]
    newAceptedFiles.splice(files.findIndex(f => f.file === file), 1);
    setFiles(newAceptedFiles);
    props.uploadedFile(undefined);
    setStatusText("");
  }

  return(
    <div style={props.style}>
      <label style={FileLabelStyle}>
        {props.label}</label>
      <label style={FileLabelStyle}>{props.text}</label>
      <div
        style={{
          display:"flex",
          flexDirection:"column",
          alignItems: "center",
          gap: "16px",
          alignSelf: "stretch",
          padding: "32px",
          justifyContent: "center",
          borderRadius: "4px",
          border: "1px dashed #616267",
          background: "#FFF",            
          width:"500px",
          height:"174px"
        }}        
        {...getRootProps({className: 'dropzone'})}
      >
        <input {...getInputProps()} />
        <Upload
          style={{
            width: "40px",
            height: "40px",
            justifyContent: "center",
            alignItems: "center",
            color:"#616267"
          }}
        />
        <div
          style={{
            display:"flex",
            flexDirection:"row",
            alignContent:"center",
            color: "#333",
            fontFeatureSettings: "'ss04' on, 'ss05' on, 'ss03' on, 'ss02' on, 'ss01' on",              
            fontFamily: "FS Elliot Pro Regular",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "28px",
          }}
        >
          Arrastra tu archivo aquí, o
          <div
            style={{
              display:"flex",
              flexDirection:"column",
              marginLeft:"3px",
            }}
          >
            <span              
              style={{
                color: "#0061A0",
                fontFeatureSettings: "'ss04' on, 'ss05' on, 'ss03' on, 'ss02' on, 'ss01' on",                
                fontFamily: "FS Elliot Pro Regular",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: "700",
                lineHeight: "28px",
                textDecoration:"none",                  
              }}
            >
              examina tu dispositivo
            </span>
            <div style={{width: '100%', height: '100%', border: '1px #0061A0 dotted'}}></div>
          </div>
        </div>
      </div>
      { files &&
        files.length > 0 &&
        <div
          style={{
            display: "flex",
            flexDirection:"column",                        
            padding: "12px 10px 12px 16px",
            gap: "12px",            
            borderRadius: "4px",
            border: "1px solid #C5C6C5",
            background: "#FFF",
            marginTop:"16px"
          }}    
        >{files.map((file,index) => 
          <div
            key={'d'+ index}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center"
            }}
          >
            <Paperclip
              style={{
                display: "flex",
                width: "20px",
                height: "20px",
                justifyContent: "center",
                alignItems: "center",
                color: "#333333"
              }}
              key={'p' + index}
            />
            <div
              style={file.error ? FileNameErrorStyle : FileNameStyle}
              key={'f' + index}
            >
              {file.file.name} - {file.file.size} bytes
            </div>
            <Button
              key={'b' + index}
              style={{
                display: "flex",
                padding: "6px",
                alignItems: "flex-start",
                gap: "4px",
                borderRadius: "100px",
                border: "1px solid rgba(51, 51, 51, 0.00)",
                background: "rgba(51, 51, 51, 0.00)",
                marginLeft:"auto",
              }}
              onClick={() => deleteFile(file.file)}
            >
              <Trash2
                key={'t' + index}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "20px",
                  height: "20px",
                  color: "#0061A0",
                  flex: "1 0 0",
                }}
              />
            </Button>
          </div>
          )
        }
        </div>
      }      
      <label style={FileLabelStyle}>
        {statusText}
      </label>
    </div>
  );
}

export default FileUpload;